import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm, useWatch } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  useCreateBuildingMutation,
  useOperatorShortQuery,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
} from '@common/cache';
import { createBuildingSchema } from '../schemas';
import { omit } from 'lodash';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { fillAddressFormValues } from '@util/google-places';

interface ICreateBuildingFormShape {
  name: string;
  organization_id?: string;
  partner_id: string;
  operator_id: string;
  google_places_id: string;
  street: string;
  suite?: string;
  city: string;
  state: string;
  county?: string;
  zip_code: string;
  phone_number?: string;
  fax_number?: string;
}

const initialValues: Partial<ICreateBuildingFormShape> = {
  name: '',
  organization_id: '',
  operator_id: '',
  partner_id: '',
  google_places_id: '',
  street: '',
  suite: '',
  city: '',
  state: '',
  county: '',
  zip_code: '',
  phone_number: '',
  fax_number: '',
};

export const useCreateBuildingModal = (
  isOpen: boolean,
  handleClose: () => void,
  handleOpenBuildingDetails: (id: string) => void,
) => {
  const { mutateAsync, isPending } = useCreateBuildingMutation();
  const { data: organizationOptions = [] } = useOrganizationsShortQuery();
  const [isSaveChangesModalOpen, setIsSaveChangesModalOpen] = useState(false);

  const formMethods = useForm<ICreateBuildingFormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<ICreateBuildingFormShape, any, any>(
      createBuildingSchema,
    ),
    defaultValues: {
      ...initialValues,
      organization_id: organizationOptions[0]?.id,
    },
  });

  const {
    handleSubmit,
    setValue,
    reset,
    getValues,
    formState: { isSubmitting, isDirty, isValid },
  } = formMethods;

  const org_id = useWatch({
    control: formMethods.control,
    name: 'organization_id',
  });
  const part_id = useWatch({
    control: formMethods.control,
    name: 'partner_id',
  });

  const { data: partnerOptions = [], refetch: refetchPartners } =
    usePartnerShortQuery(org_id as string);
  const { data: operatorOptions = [], refetch: refetchOperators } =
    useOperatorShortQuery(part_id);

  useEffect(() => {
    if (org_id && isOpen) {
      refetchPartners();
    }
  }, [org_id, isOpen]);

  useEffect(() => {
    if (org_id && isOpen && part_id) {
      refetchOperators();
    }
  }, [part_id]);

  const onCloseAction = () => {
    if (isDirty) {
      return setIsSaveChangesModalOpen(true);
    }
    handleClose();
    reset();
  };

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    fillAddressFormValues({ setValue, place });
  };

  const submitHandler = async (values: ICreateBuildingFormShape) => {
    try {
      const response = await mutateAsync({
        ...omit(values, ['organization_id']),
        country: 'US',
        record_id: values.operator_id,
        phone_number: values.phone_number || '',
      });

      if (response.data.id) {
        handleOpenBuildingDetails(response.data.id);
      }

      handleClose();
      reset();
      toast.success('You added a new building');
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleCloseSaveChangesModal = () => {
    setIsSaveChangesModalOpen(false);
  };

  const handleDiscardChanges = () => {
    setIsSaveChangesModalOpen(false);
    handleClose();
    reset();
  };

  const handleSaveChanges = async () => {
    const values = getValues();

    try {
      await mutateAsync({
        ...omit(values, ['organization_id']),
        country: 'US',
        record_id: values.operator_id,
        phone_number: values.phone_number || '',
      });

      toast.success('You added a new building');
      setIsSaveChangesModalOpen(false);
      handleClose();
      reset();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  return {
    organizationOptions: organizationOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    partnerOptions: partnerOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    operatorOptions: operatorOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    formMethods,
    onSubmit,
    isSubmitting,
    submitHandler,
    handleSubmit,
    onCloseAction,
    isPartnerSelectDisabled: organizationOptions.length > 0 && !org_id,
    isOperatorSelectDisabled: !part_id,
    setValue,
    handleSelectLocation,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleSaveChanges,
    handleDiscardChanges,
    isValid,
    isEditSubmitting: isPending,
  };
};
